import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { usePersistedViewMode } from "@/utils/viewMode";

interface ListsFilterContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  showFavoritesOnly: boolean;
  setShowFavoritesOnly: (show: boolean) => void;
  sortField: "name" | "dateCreated" | "candidateCount";
  setSortField: (field: "name" | "dateCreated" | "candidateCount") => void;
  sortOrder: "asc" | "desc";
  setSortOrder: (order: "asc" | "desc") => void;
  viewMode: "card" | "list";
  setViewMode: (mode: "card" | "list") => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

const ListsFilterContext = createContext<ListsFilterContextType | undefined>(undefined);

interface ListsFilterProviderProps {
  children: ReactNode;
  viewMode: "card" | "list";
  setViewMode: (mode: "card" | "list") => void;
}

export const ListsFilterProvider = ({ children, viewMode, setViewMode }: ListsFilterProviderProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	
	// Initialize state from URL parameters
	const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
	const [showFavoritesOnly, setShowFavoritesOnly] = useState(() => searchParams.get("favoritesOnly") === "true");
	const [sortField, setSortField] = useState<"name" | "dateCreated" | "candidateCount">(() => {
		const field = searchParams.get("sortField");
		return (field as "name" | "dateCreated" | "candidateCount") || "dateCreated";
	});
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">(() => {
		const order = searchParams.get("sortOrder");
		return (order as "asc" | "desc") || "desc";
	});
	const [currentPage, setCurrentPage] = useState(() => {
		const page = searchParams.get("page");
		return page ? parseInt(page, 10) : 1;
	});

	// Update URL parameters whenever filters change
	useEffect(() => {
		const params = new URLSearchParams(searchParams);
		
		// Clear only filter-related params
		params.delete("search");
		params.delete("favoritesOnly");
		params.delete("sortField");
		params.delete("sortOrder");
		params.delete("page");
		
		if (searchTerm)
			params.set("search", searchTerm);
		if (showFavoritesOnly)
			params.set("favoritesOnly", "true");
		if (sortField !== "dateCreated")
			params.set("sortField", sortField);
		if (sortOrder !== "desc")
			params.set("sortOrder", sortOrder);
		if (currentPage !== 1)
			params.set("page", currentPage.toString());
		
		setSearchParams(params, { replace: true });
	}, [searchTerm, showFavoritesOnly, sortField, sortOrder, currentPage, searchParams]);

	const value = useMemo(() => ({
		searchTerm,
		setSearchTerm,
		showFavoritesOnly,
		setShowFavoritesOnly,
		sortField,
		setSortField,
		sortOrder,
		setSortOrder,
		viewMode,
		setViewMode,
		currentPage,
		setCurrentPage,
	}), [searchTerm, showFavoritesOnly, sortField, sortOrder, viewMode, setViewMode, currentPage]);

  return (
    <ListsFilterContext.Provider value={value}>
      {children}
    </ListsFilterContext.Provider>
  );
};

export const useListsFilter = () => {
  const context = useContext(ListsFilterContext);
  if (context === undefined) {
    throw new Error("useListsFilter must be used within a ListsFilterProvider");
  }
  return context;
};
